import { Currency, type SuretyAccountId, SuretyAccountStatus } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	useFetchProjectedAccountExposure,
	useFetchSuretyAccount,
	useFetchSuretyAccountBondingLines,
} from "@portal/api/account";
import { Decimal } from "decimal.js-light";
import { type ReactNode, useMemo } from "react";
import { normalizeToError } from "../../../utils/error";
import { cn } from "../../lib/utils";
import { formatMetricCardCurrency, MetricCard } from "../../patterns/metric-card";
import { Icon, Text } from "../../primitives";
import { CenteredSpinner } from "../../ui/CenteredSpinner";
import { PortaledTooltip } from "../../ui/Tooltip";
import { ReportedRetryableLoadError } from "../load-errors";
import { buildExposureBreakdownViews } from "./buildExposureBreakdownViews";
import { ExposureWithTerms } from "./ExposureWithTerms";
import type { ExposureBreakdownView } from "./types";

export type AgentExposureSectionProps = {
	accountId: SuretyAccountId;
	testId?: string;
};

const IN_REVIEW = "In Review";
const PER_REQUEST = "Per request";

type AgentAvailableSummary = {
	label: string;
	value: string;
	/** Limit minus committed exposure. Absent when there is no aggregate limit to measure. */
	remaining: Option<number>;
};

type AgentExposureTermsProps = {
	rate?: number;
	singleLimit?: number;
	aggregateLimit?: number;
	isReview: boolean;
	available?: AgentAvailableSummary;
	testId: string;
};

const usedAmount = (view: ExposureBreakdownView) =>
	Currency.sum(view.columns.flatMap((column) => column.segments).map((segment) => segment.amount));

const COMMITTED_SEGMENT_IDS = new Set(["open-bid", "approved-bid", "open-final", "approved-final"]);

/** Bid and final, open and approved, added once. Requested stays out. */
const committedAmount = (view: ExposureBreakdownView) =>
	Currency.sum(
		view.columns.flatMap((column) =>
			column.segments
				.filter((segment) => COMMITTED_SEGMENT_IDS.has(segment.id))
				.map((segment) => segment.amount),
		),
	);

const availableSummary = (
	view: ExposureBreakdownView | undefined,
	aggregateLimit: number | undefined,
): AgentAvailableSummary | undefined => {
	if (Option.isNone(aggregateLimit)) {
		return { label: "Available", value: "N/A", remaining: undefined };
	}
	if (view === undefined) return undefined;
	const remaining = Currency.subtract(aggregateLimit, committedAmount(view));
	return {
		label: "Available",
		value: formatMetricCardCurrency(remaining).display,
		remaining,
	};
};

const formatRate = (rate: Option<number>) =>
	Option.mapOrElse(
		rate,
		(value) => {
			const percent = new Decimal(value).times(100).toDecimalPlaces(3);
			const places = percent.toFixed(3).endsWith("0") ? 2 : 3;
			return `${percent.toFixed(places)}%`;
		},
		"N/A",
	);

const moneyLabel = (amount: Option<number>) =>
	Option.mapOrElse(amount, (value) => formatMetricCardCurrency(value).display, PER_REQUEST);

const availableTone = (available: AgentAvailableSummary): "danger" | "confirm" | undefined => {
	if (Option.isNone(available.remaining)) return undefined;
	if (available.remaining < 0) return "danger";
	if (available.remaining > 0) return "confirm";
	return undefined;
};

const valueToneClass = (tone: "danger" | "confirm" | undefined) => {
	if (tone === "danger") return "text-danger";
	if (tone === "confirm") return "text-confirm";
	return undefined;
};

const AvailableFormulaTooltip = ({ testId }: { testId: string }) => {
	const tooltipId = `${testId}-available-tooltip`;
	return (
		<>
			<button
				type="button"
				className="inline-flex cursor-help text-tertiary"
				aria-label="Requested is not included"
				data-tooltip-id={tooltipId}
				data-testid={`${testId}-available-info`}
			>
				<Icon type="information-circle" size="mini" color="tertiary" />
			</button>
			<PortaledTooltip id={tooltipId} place="bottom" content="Requested is not included" />
		</>
	);
};

const TermMetric = ({
	label,
	value,
	testId,
	valueTestId,
	tone,
	labelAdornment,
}: {
	label: string;
	value: string;
	testId: string;
	valueTestId?: string;
	tone?: "danger" | "confirm";
	labelAdornment?: ReactNode;
}) => (
	<div className="flex flex-col items-start gap-1.5" data-testid={testId}>
		<div className="flex items-center gap-1">
			<Text type="eyebrow" color="tertiary" className="flex">
				{label}
			</Text>
			{labelAdornment}
		</div>
		<MetricCard.Value size="md" className={valueToneClass(tone)} data-testid={valueTestId}>
			{value}
		</MetricCard.Value>
	</div>
);

const AgentExposureTerms = ({
	rate,
	singleLimit,
	aggregateLimit,
	isReview,
	available,
	testId,
}: AgentExposureTermsProps) => (
	<div className="w-full" data-testid={`${testId}-terms`}>
		<div
			className={cn("grid w-full gap-x-6", available === undefined ? "grid-cols-3" : "grid-cols-4")}
		>
			{available !== undefined && (
				<TermMetric
					label={available.label}
					value={available.value}
					tone={availableTone(available)}
					labelAdornment={
						Option.isSome(available.remaining) ? (
							<AvailableFormulaTooltip testId={testId} />
						) : undefined
					}
					testId={`${testId}-limit`}
					valueTestId={`${testId}-limit-value`}
				/>
			)}
			<TermMetric
				label="Single"
				value={isReview ? IN_REVIEW : moneyLabel(singleLimit)}
				testId={`${testId}-terms-single`}
			/>
			<TermMetric
				label="Aggregate"
				value={isReview ? IN_REVIEW : moneyLabel(aggregateLimit)}
				testId={`${testId}-terms-aggregate`}
			/>
			<TermMetric
				label="Rate"
				value={isReview ? IN_REVIEW : formatRate(rate)}
				testId={`${testId}-terms-rate`}
			/>
		</div>
	</div>
);

export type AgentExposureCardProps = {
	categoryView?: ExposureBreakdownView;
	rate?: number;
	singleLimit?: number;
	aggregateLimit?: number;
	isReview: boolean;
	/** Replaces the chart (loading or error state); the Available summary still uses `categoryView`. */
	body?: ReactNode;
	testId?: string;
};

export const AgentExposureCard = ({
	categoryView,
	rate,
	singleLimit,
	aggregateLimit,
	isReview,
	body,
	testId = "agent-exposure",
}: AgentExposureCardProps) => {
	const summary = availableSummary(categoryView, aggregateLimit);
	const chartView = body === undefined ? categoryView : undefined;
	return (
		<ExposureWithTerms
			views={chartView === undefined ? undefined : [chartView]}
			terms={
				<AgentExposureTerms
					rate={rate}
					singleLimit={singleLimit}
					aggregateLimit={aggregateLimit}
					isReview={isReview}
					available={summary}
					testId={testId}
				/>
			}
			breakdownCaption={chartView === undefined ? undefined : "By Category"}
			body={body}
			testId={testId}
		/>
	);
};

export const AgentExposureSection = ({
	accountId,
	testId = "agent-exposure",
}: AgentExposureSectionProps) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { bondingLines, bondingLinesIsLoading, bondingLinesError, refetchBondingLines } =
		useFetchSuretyAccountBondingLines(accountId);
	const bondingLinesArePublished =
		Option.isSome(bondingLines) && Option.isSome(bondingLines.aggregateLimit);
	const { exposure, exposureError, exposureIsLoading, refetchExposure } =
		useFetchProjectedAccountExposure(accountId, Option.isSome(bondingLines));

	const categoryView = useMemo(() => {
		if (exposure === undefined) return undefined;
		return buildExposureBreakdownViews(exposure, {
			limitVariant: "agent",
			aggregateLimit: bondingLines?.aggregateLimit,
		}).find((view) => view.id === "category");
	}, [bondingLines?.aggregateLimit, exposure]);

	if (bondingLinesIsLoading || suretyAccountIsLoading) {
		return (
			<div className="min-h-40">
				<CenteredSpinner testId={`${testId}-spinner`} />
			</div>
		);
	}

	if (bondingLinesError) {
		return (
			<ReportedRetryableLoadError
				error={normalizeToError(bondingLinesError)}
				resource="bonding lines"
				onRetry={refetchBondingLines}
				testId={`${testId}-error`}
			/>
		);
	}

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={normalizeToError(suretyAccountError)}
				resource="account"
				onRetry={refetchSuretyAccount}
				testId={`${testId}-error`}
			/>
		);
	}

	if (!suretyAccount || !bondingLines || suretyAccount.status === SuretyAccountStatus.declined) {
		return null;
	}

	const hasExposure = categoryView !== undefined && usedAmount(categoryView) > 0;
	const body = exposureIsLoading ? (
		<div className="min-h-40">
			<CenteredSpinner testId={`${testId}-spinner`} />
		</div>
	) : exposureError ? (
		<ReportedRetryableLoadError
			error={normalizeToError(exposureError)}
			resource="exposure"
			onRetry={refetchExposure}
			testId={`${testId}-chart-error`}
		/>
	) : undefined;
	const showChart =
		body === undefined && categoryView !== undefined && (bondingLinesArePublished || hasExposure);

	return (
		<AgentExposureCard
			categoryView={showChart || exposureError ? categoryView : undefined}
			rate={bondingLines.rate}
			singleLimit={bondingLines.singleLimit}
			aggregateLimit={bondingLines.aggregateLimit}
			isReview={suretyAccount.status === SuretyAccountStatus.review}
			body={body}
			testId={testId}
		/>
	);
};
